import React from 'react';
import { Box, Typography } from '@mui/material';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import OverlaySpinner from '@/components/OverlaySpinner';
import { ServiceType } from '@/__generated__/globalTypes';
import getConfig from 'next/config';
import { SxClassProps } from '@/types/common';

const classes: SxClassProps = {
  messageContainerStyle: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
  },
  messageStyle: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.care?.navy[600],
    width: 198,
    height: 198,
    borderRadius: 100,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  }),
  messageTextStyle: {
    top: '22%',
    left: '18%',
    width: 125,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  descriptionStyle: {
    lineHeight: '17px',
    fontWeight: 'bold',
  },
};

const defaultMapOptions = {
  disableDefaultUI: true,
  minZoom: 13,
};

const {
  publicRuntimeConfig: { GOOGLE_MAPS_KEY },
} = getConfig();

const defaultLatitude = 37.773972;
const defaultLongitude = -122.431297;

const containerStyle = {
  height: 290,
  maxWidth: 410,
};

interface MapProps {
  count?: number;
  isSeeker?: boolean;
  lat?: number | null;
  lng?: number | null;
  serviceType: ServiceType;
}

function Map({ count, lat, lng, serviceType, isSeeker }: MapProps) {
  if (!window) return null;

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_KEY,
  });

  const serviceToTextMapping: Record<ServiceType, string> = {
    AU_PAIR: 'au pair',
    CARE_GIGS: 'care gigs',
    CHILD_CARE: 'child care',
    GENERIC: 'generic',
    HOUSEKEEPING: 'housekeeping',
    PET_CARE: 'pet care',
    SENIOR_CARE: 'senior care',
    SPECIAL_NEEDS: 'special needs',
    TUTORING: 'tutoring',
  };

  const seekerDescriptionText = `${serviceToTextMapping[serviceType]} caregivers near you!`;

  const providerDescriptionText = 'families seeking care near you.';

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={{ lat: lat || defaultLatitude, lng: lng || defaultLongitude }}
      zoom={13}
      options={defaultMapOptions}>
      <>
        {Number.isInteger(count) ? (
          <Box sx={classes.messageContainerStyle} data-testid="map-label">
            <Box sx={classes.messageStyle}>
              <Typography variant="subtitle2" sx={classes.messageTextStyle}>
                <Typography variant="display1" margin="0">
                  {count || 0}
                </Typography>
                <Box component="span" sx={classes.descriptionStyle}>
                  {isSeeker ? seekerDescriptionText : providerDescriptionText}
                </Box>
              </Typography>
            </Box>
          </Box>
        ) : null}
      </>
    </GoogleMap>
  ) : (
    <OverlaySpinner isOpen wrapped />
  );
}

Map.defaultProps = {
  count: undefined,
  isSeeker: false,
  lat: defaultLatitude,
  lng: defaultLongitude,
};

export default Map;
